import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

export default function SponsorsAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/sponsors").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete sponsor?")) return;
    await api.delete(`/sponsors/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">Sponsors</h1>
          <p className="text-[#7A7D8C] mt-1 text-sm">Manage sponsors displayed on the public site.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-sponsor-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Plus className="w-4 h-4" /> Add sponsor</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="col-span-full bg-white rounded-2xl border border-slate-100 p-12 text-center text-[#7A7D8C]">No sponsors yet.</div>}
        {rows.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`sponsor-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{s.name}</div>
                <div className="text-xs uppercase tracking-wider text-[#7A7D8C] mt-1">{s.tier}</div>
              </div>
              <button onClick={() => remove(s.id)} data-testid={`sponsor-delete-${s.id}`} className="p-2 rounded-lg text-[#E71D36] hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
            </div>
            {s.description && <p className="mt-3 text-sm text-[#7A7D8C]">{s.description}</p>}
            {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs text-[#FF9F1C] font-semibold">{s.website}</a>}
            {!s.active && <div className="mt-3 text-[10px] uppercase font-bold text-[#7A7D8C]">Hidden</div>}
          </div>
        ))}
      </div>

      {open && <AddForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function AddForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", website: "", tier: "supporter", logo_url: "", description: "", active: true });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/sponsors", f);
      toast.success("Sponsor added");
      onSaved();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
      <form onSubmit={save} className="bg-white rounded-3xl max-w-md w-full p-8 relative" data-testid="sponsor-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        <h2 className="font-display font-black text-2xl">Add sponsor</h2>
        <div className="mt-6 space-y-4">
          <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required tid="sp-name" />
          <Field label="Website" value={f.website} onChange={(v) => setF({ ...f, website: v })} tid="sp-website" />
          <Field label="Logo URL" value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} tid="sp-logo" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Tier</label>
            <select value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="sp-tier" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="supporter">Supporter</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Description</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} data-testid="sp-desc" className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="sp-active" className="w-4 h-4 accent-[#FF9F1C]" /> Visible on public site
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} data-testid="sp-save" className="px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold disabled:opacity-60">{saving ? "…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
    </div>
  );
}
